import { motion } from 'framer-motion';

interface BayWindowsProps {
  city: string;
}

export function BayWindows({ city }: BayWindowsProps) {
  const bayTypes = [
    {
      icon: 'ri-arrow-left-right-line',
      title: 'Baie Coulissante',
      description: 'Système à rails avec vantaux qui glissent horizontalement. Idéale pour les grandes ouvertures.',
      repairs: 'Remplacement roulettes, réglage rails, changement joints',
    },
    {
      icon: 'ri-layout-column-line',
      title: 'Baie à Galandage',
      description: 'Les vantaux disparaissent dans le mur pour une ouverture totale sans encombrement.',
      repairs: 'Réparation mécanisme escamotable, rails, étanchéité',
    },
    {
      icon: 'ri-layout-grid-line',
      title: 'Baie Pliante',
      description: 'Système accordéon permettant une ouverture modulable et un gain de place optimal.',
      repairs: 'Charnières, pivots, alignement vantaux, serrures',
    },
    {
      icon: 'ri-stop-line',
      title: 'Baie Fixe',
      description: 'Vitrage non ouvrant offrant une vue panoramique et une luminosité maximale.',
      repairs: 'Remplacement vitrage, joints d\'étanchéité, isolation',
    },
    {
      icon: 'ri-arrow-up-down-line',
      title: 'Baie Oscillo-Battante',
      description: 'Double système d\'ouverture : battante classique ou soufflet par le haut.',
      repairs: 'Mécanisme oscillo-battant, ferrures, poignées, joints',
    },
    {
      icon: 'ri-drag-move-line',
      title: 'Baie Levante-Coulissante',
      description: 'Système haut de gamme avec levage du vantail pour un coulissement ultra-fluide.',
      repairs: 'Mécanisme de levage, roulettes, rails, réglages précis',
    },
  ];

  const interventions = [
    {
      icon: 'ri-settings-3-line',
      title: 'Roulettes et Rails',
      description: 'Remplacement des roulettes usées, nettoyage et réparation des rails pour un coulissement parfait.',
    },
    {
      icon: 'ri-window-line',
      title: 'Vitrage',
      description: 'Remplacement de vitrage cassé, fissuré ou embué. Double et triple vitrage toutes dimensions.',
    },
    {
      icon: 'ri-drop-line',
      title: 'Joints d\'Étanchéité',
      description: 'Changement des joints pour éliminer les infiltrations d\'eau et d\'air, améliorer l\'isolation.',
    },
    {
      icon: 'ri-tools-line',
      title: 'Réglage et Ajustement',
      description: 'Réglage précis des vantaux, ajustement de la hauteur et de l\'alignement pour un fonctionnement optimal.',
    },
    {
      icon: 'ri-lock-line',
      title: 'Serrures et Poignées',
      description: 'Réparation ou remplacement des systèmes de fermeture, poignées cassées, serrures multipoints.',
    },
    {
      icon: 'ri-temp-cold-line',
      title: 'Isolation',
      description: 'Amélioration de l\'isolation thermique et acoustique, traitement des ponts thermiques.',
    },
  ];

  return (
    <section id="baies-vitrees" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Réparation de Baies Vitrées à {city}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-6">
            Spécialiste de la réparation de baies vitrées toutes marques : Technal, K-Line, Schüco, Reynaers, Kawneer, Veka, Rehau, Solarlux
          </p>
          <div className="inline-block bg-teal/10 border-2 border-teal rounded-2xl px-6 py-3">
            <span className="text-teal font-bold text-lg">Toutes Formes • Toutes Marques • Intervention Rapide</span>
          </div>
        </motion.div>

        <div className="mb-20">
          <h3 className="text-3xl font-bold text-gray-900 mb-10 text-center">Types de Baies Vitrées</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {bayTypes.map((type, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-14 h-14 bg-teal/10 rounded-xl flex items-center justify-center mb-4">
                  <i className={`${type.icon} text-teal text-2xl`}></i>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">{type.title}</h4>
                <p className="text-gray-600 mb-4 leading-relaxed">{type.description}</p>
                <div className="pt-4 border-t border-gray-100">
                  <div className="text-sm font-medium text-teal mb-2">Réparations courantes :</div>
                  <div className="text-sm text-gray-600">{type.repairs}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <div>
          <h3 className="text-3xl font-bold text-gray-900 mb-10 text-center">Nos Interventions sur Baies Vitrées</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {interventions.map((intervention, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 border-2 border-gray-100 hover:border-teal transition-all duration-300"
              >
                <div className="w-14 h-14 bg-orange/10 rounded-xl flex items-center justify-center mb-4">
                  <i className={`${intervention.icon} text-orange text-2xl`}></i>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">{intervention.title}</h4>
                <p className="text-gray-600 leading-relaxed">{intervention.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 bg-gradient-to-r from-orange to-orange-dark rounded-3xl p-12 text-center text-white"
        >
          <i className="ri-tools-fill text-6xl mb-6 opacity-80"></i>
          <h3 className="text-3xl font-bold mb-4">Expert Baies Vitrées à {city}</h3>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Plus de 15 ans d'expérience dans la réparation de baies vitrées. Nous intervenons sur toutes les marques et tous les systèmes. Diagnostic gratuit et devis détaillé.
          </p>
          <div className="flex flex-wrap justify-center gap-4 text-sm font-medium">
            <span className="bg-white/20 px-4 py-2 rounded-full">Technal</span>
            <span className="bg-white/20 px-4 py-2 rounded-full">K-Line</span>
            <span className="bg-white/20 px-4 py-2 rounded-full">Schüco</span>
            <span className="bg-white/20 px-4 py-2 rounded-full">Reynaers</span>
            <span className="bg-white/20 px-4 py-2 rounded-full">Kawneer</span>
            <span className="bg-white/20 px-4 py-2 rounded-full">Veka</span>
            <span className="bg-white/20 px-4 py-2 rounded-full">Rehau</span>
            <span className="bg-white/20 px-4 py-2 rounded-full">Solarlux</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}